
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageSquare, Calendar, CheckCircle, Clock } from 'lucide-react';
import CountdownTimer from '@/components/CountdownTimer';

const Welcome = () => {
  const [firstName, setFirstName] = useState('');
  const navigate = useNavigate();
  
  useEffect(() => {
    // Retrieve user data from session storage
    const userDataString = sessionStorage.getItem('userData');
    
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setFirstName(userData.firstName || '');
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    } else {
      // If no user data, redirect back to opt-in page
      navigate('/');
    }
  }, [navigate]);
  
  const handleJoinFacebookGroup = () => {
    window.open('https://www.facebook.com/groups/1028510719227419', '_blank');
  };

  // Webinar date for countdown timer
  const webinarDate = new Date('May 29, 2025 15:00:00 GMT+0100');
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <span className="highlight-badge mb-4 bg-blue-100 text-blue-800 inline-block px-4 py-1 rounded-full text-sm font-bold">AI KIDS ACADEMY</span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              🎉 You're In, {firstName}! Here's How to Give Your Child an Unfair Advantage in the Age of AI
            </h1>
          </div>
          
          {/* Motivation Booster */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 rounded-lg text-white text-center mb-8">
            <p className="text-lg font-medium">
              Most parents won't take this step. You just did.<br/>
              <span className="font-bold">That's what sets future-ready families apart.</span>
            </p>
          </div>
          
          {/* Steps Section */}
          <div className="bg-white p-6 rounded-xl shadow-lg mb-10">
            <div className="space-y-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Step 1</h3>
                  <p className="text-gray-700">
                    <span className="font-bold text-red-600">REQUIRED:</span> Join our exclusive Facebook group where we'll share all webinar details, links, and updates.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-purple-100 rounded-full p-2">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Step 2</h3>
                  <p className="text-gray-700">
                    📅 Add the webinar to your calendar right now — this is your child's first step into the future.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-green-100 rounded-full p-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Step 3</h3>
                  <p className="text-gray-700">
                    📬 Check your inbox — we just sent your confirmation and next steps (look in spam/promotions if needed).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-amber-100 rounded-full p-2">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Step 4</h3>
                  <p className="text-gray-700">
                    📺 Show up LIVE on Thursday, 29th May @ 3:00 PM BST — this is a 60-minute webinar for parents. What you'll learn could change your child's future.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="font-bold text-red-800">
                ⚠️ IMPORTANT: You MUST join our Facebook group below to receive all webinar details and updates!
              </p>
            </div>
            
            {/* CTA Buttons */}
            <div className="space-y-4">
              <button 
                onClick={handleJoinFacebookGroup}
                className="cta-button w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 py-4 rounded-lg text-white text-lg font-bold transition-colors animate-pulse"
              >
                <MessageSquare className="w-5 h-5" />
                <span>JOIN THE FACEBOOK GROUP NOW (REQUIRED)</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mt-4 text-center text-gray-700 font-medium">
              💬 All webinar access details will be shared ONLY in the Facebook group.
            </div>
            
            <div className="mt-6">
              <CountdownTimer 
                targetDate={webinarDate} 
                label="⏰ Webinar starts in:" 
              />
            </div>
            
            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-center text-gray-700 font-medium">
                📌 Show up ready. Take notes. Ask questions.<br/>
                Because what your child does after this — will look nothing like what they did before.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
